/**
 * アプリケーション全体のルートパス定義
 * 型安全性と一元管理を提供
 */
export const PATHS = {
  HOME: '/',
  SIGN_OUT: '/sign-out',
  ACTIVITY: '/activity',
  PLACES: '/places',
  TRIPS: {
    LIST: '/trips',
    DETAIL: '/trips/:trip_id',
    HIGHLIGHT: '/trips/:trip_id/highlight',
  },
  RALLIES: {
    LIST: '/rallies',
    DETAIL: '/rallies/:rally_id',
    IMPORT: '/rallies/:rally_id/import',
  },
  ROUTE_PLANS: {
    LIST: '/route-plans',
    CREATE: '/route-plans/new',
    DETAIL: '/route-plans/:plan_id',
  },
  CUSTOM_VIDEO: '/custom-video',
  CUSTOM_MUSIC: '/custom-music',
  GRAMMAR_CHECK: '/grammar-check',
  STORAGE_MANAGEMENT: '/storage-management',
  ADMIN: {
    USERS: '/admin/users',
  },
  // 新しいパス構造: /p?domain=xxx (リスト) と /p/:episode_id (リーダー)
  P: {
    LIST: '/p', // クエリパラメータ ?domain=xxx で使用
    EPISODE: '/p/:episode_id',
  },
  // 独立パス
  CHECK: '/check',
  SHARE: '/share',
  DOWNLOAD_QUEUE: '/notifications?tab=queue',
  OFFLINE_PRODUCTS: '/offline-products',
  WORKOUT: {
    HOME: '/workout',
    EXERCISES: '/workout/exercises',
    TEMPLATES: '/workout/templates',
    SESSION: '/workout/sessions/:session_id',
  },
  NUTRITION: {
    HOME: '/nutrition',
  },
  SETTINGS: {
    INTEGRATIONS: '/settings/integrations',
    EUFY: '/settings/integrations/eufy',
    HA: '/settings/integrations/ha',
    API_USAGE: '/settings/api-usage',
    AI_USAGES: '/settings/ai-usages',
    PASSKEYS: '/settings/passkeys',
  },
  EUFY: {
    HOME: '/eufy',
  },
  SLEEP: {
    // 対象の睡眠は ?id=<睡眠ID> クエリで指定する（省略時は最新）
    HOME: '/sleep',
  },
  TODO: {
    HOME: '/todo',
  },
  FLASHCARD: {
    REVIEW: '/flashcard-review',
  },
  MEMO: {
    // /memo 単一エントリ。対象メモは ?id=<memoId> クエリで指定する
    LIST: '/memo',
    // 作品（本）ごとの読書ノート
    BOOK_NOTE: '/memo/books/:product_id',
    // メモ・本・カードのつながりの全体図
    GRAPH: '/memo/graph',
  },
  LIBRARY: {
    // 図書館 (Calil) 統合検索。まず本を検索して選択する画面
    SEARCH: '/library/search',
  },
  MUSIC: {
    // 新しい音楽画面のライブラリトップ
    LIBRARY: '/music',
    ARTISTS: '/music/artists',
    ARTIST_DETAIL: '/music/artists/:tag_id',
    ALBUM_DETAIL: '/music/albums/:product_id',
    SONGS: '/music/songs',
    PLAYLISTS: '/music/playlists',
    PLAYLIST_DETAIL: '/music/playlists/:playlist_id',
    // ダウンロード済みの入口。配下の一覧は ?downloaded=1 付きで既存画面を再利用する
    DOWNLOADED: '/music/downloaded',
  },
  RECEIPTS: {
    // レシートのレビューキュー。対象レシートは ?id=<receiptId> クエリで指定する
    LIST: '/receipts',
  },
  IMPORT: '/import',
  NOTIFICATIONS: '/notifications',
} as const

export const bookNotePath = (productId: number): string =>
  PATHS.MEMO.BOOK_NOTE.replace(':product_id', String(productId))

export const memoPath = (memoId: number): string => `${PATHS.MEMO.LIST}?id=${memoId}`

/** リーダーを開いてすぐ移動する AI 読書アシストの位置キーのクエリ名 */
export const READER_AT_PARAM = 'at'

/** 本をリーダーで開き、AI 読書アシストの位置キー（EPUB: spine 番号 + 進み具合 / PDF: 0 始まりのページ）へ移動する */
export const readerPositionPath = (episodeId: number, key: number): string =>
  `${PATHS.P.EPISODE.replace(':episode_id', String(episodeId))}?${READER_AT_PARAM}=${key}`

/** TextDocument の本文を開く画面（memo / product_note / todo） */
export const textDocumentPath = (entityType: string, entityId: number): string | null => {
  if (entityType === 'memo') return memoPath(entityId)
  if (entityType === 'product_note') return bookNotePath(entityId)
  if (entityType === 'todo') return `${PATHS.TODO.HOME}?todo=${entityId}`
  return null
}
